import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { SavePurchaseOrderInput } from '@/features/procurement/hooks';

export interface DownstreamEffect {
  area: string;
  detail: string;
}

export interface DownstreamPreview {
  id?: string;
  severity: 'none' | 'minor' | 'major';
  title: string;
  summary: string;
  effects: DownstreamEffect[];
}

export function asDownstreamPreview(data: unknown): DownstreamPreview {
  const row = (data ?? {}) as Partial<DownstreamPreview>;
  const severity = row.severity === 'major' || row.severity === 'minor' ? row.severity : 'none';
  return {
    id: row.id,
    severity,
    title: row.title ?? '',
    summary: row.summary ?? '',
    effects: Array.isArray(row.effects)
      ? row.effects.filter((e) => e && typeof e.detail === 'string')
      : [],
  };
}

function invalidateCorrected(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  qc.invalidateQueries({ queryKey: ['receiving-lines'] });
  qc.invalidateQueries({ queryKey: ['item-po-lines'] });
  qc.invalidateQueries({ queryKey: ['item-cost-summaries'] });
  qc.invalidateQueries({ queryKey: ['item-cost-history'] });
  qc.invalidateQueries({ queryKey: ['stock-movements'] });
  qc.invalidateQueries({ queryKey: ['inventory-levels'] });
  qc.invalidateQueries({ queryKey: ['lots'] });
  qc.invalidateQueries({ queryKey: ['returns'] });
  qc.invalidateQueries({ queryKey: ['production-forms'] });
}

export function usePreviewPurchaseOrderChange() {
  return useMutation({
    mutationFn: async (input: SavePurchaseOrderInput) => {
      const { lines, ...header } = input;
      const { data, error } = await supabase.rpc(
        'preview_purchase_order_change' as never,
        {
          p_po: header,
          p_lines: lines,
        } as never,
      );
      if (error) throw error;
      return asDownstreamPreview(data);
    },
  });
}

export interface ReceivingCorrectionInput {
  gr_line_id: string;
  quantity?: number;
  item_id?: string;
  to_location_id?: string;
  unit_cost_cents?: number | null;
  notes?: string | null;
  received_at?: string | null;
  packing_slip_number?: string | null;
  receipt_notes?: string | null;
  reverse?: boolean;
  reverse_receipt?: boolean;
  movement_memo?: string | null;
}

async function rpcPreview(fn: string, args: Record<string, unknown>) {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw error;
  return asDownstreamPreview(data);
}

export function usePreviewReceivingCorrection() {
  return useMutation({
    mutationFn: (input: ReceivingCorrectionInput) =>
      rpcPreview('preview_receiving_correction', { p: input }),
  });
}

export function useCorrectReceivingLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReceivingCorrectionInput) => {
      const { data, error } = await supabase.rpc(
        'correct_receiving_line' as never,
        { p: input } as never,
      );
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => invalidateCorrected(qc),
  });
}

export interface StockMovementCorrectionInput {
  id: string;
  quantity?: number;
  item_id?: string;
  from_location_id?: string | null;
  to_location_id?: string | null;
  memo?: string | null;
  reason_code?: string | null;
  reverse?: boolean;
}

export function usePreviewStockMovementCorrection() {
  return useMutation({
    mutationFn: (input: StockMovementCorrectionInput) =>
      rpcPreview('preview_stock_movement_correction', { p: input }),
  });
}

export function useCorrectStockMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StockMovementCorrectionInput) => {
      const { data, error } = await supabase.rpc(
        'correct_stock_movement' as never,
        { p: input } as never,
      );
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => invalidateCorrected(qc),
  });
}
